import React, { useState, useEffect, useCallback, useRef } from 'react';

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';

const ACCENT = '#fa163b';
const ACCENT_HOVER = '#d41233';
const BG = '#0a0a0a';
const SURFACE = '#161616';
const SURFACE_2 = '#1e1e1e';
const BORDER = 'rgba(255,255,255,0.08)';
const BORDER_HOVER = 'rgba(255,255,255,0.15)';
const TEXT = '#fff';
const TEXT_MID = 'rgba(255,255,255,0.6)';
const TEXT_DIM = 'rgba(255,255,255,0.35)';

const STAGE_COLORS = {
  'Scouted': '#6b7280', 'Contact Identified': '#60a5fa', 'First Contact': '#3b82f6',
  'Contact Responded': '#22c55e', 'Second Contact': '#a78bfa', 'Third Contact': '#c084fc',
  'Fourth Contact': '#e879f9', 'Qualification Meeting': '#f59e0b', 'Interview / Visit': '#f97316',
  'Offer': '#10b981', 'Won': '#059669', 'Lost': '#ef4444', 'No Response': '#525252'
};

const CAT_COLORS = {
  'Bulge Bracket Bank': '#60a5fa', 'Commodity Broker / IDB': '#34d399',
  'Commodity Trading House': '#fbbf24', 'Metals Specialist': '#a78bfa',
  'Prop Trading / Market Maker': '#f87171', 'Regional / Specialist Bank': '#22d3ee'
};

const TIER_LABELS = {
  1: 'Early Careers / Grad Recruitment',
  2: 'Head of Desk / MD',
  3: 'Senior Trader / VP',
  4: 'HR Business Partner'
};

const DEFAULT_PRIORITY_WEIGHTS = {
  accessibility: 1,
  relevance: 1,
  scale: 1,
  competition: 1,
  warm_path: 1
};

const DEFAULT_APOLLO_ROLE_CONFIG = {
  titles: ['head of trading', 'trader', 'graduate recruitment', 'early careers', 'hr', 'managing director', 'commodities'],
  seniorities: ['head', 'director', 'vp', 'manager', 'senior']
};

const Footer = () => (
  <footer style={{ padding: '32px 16px 24px', textAlign: 'center', borderTop: `1px solid ${BORDER}` }}>
    <img src="/bejo-logo-light.png" alt="Bejo" style={{ height: 18, opacity: 0.35 }} />
  </footer>
);

function App() {
  const [view, setView] = useState('kanban');
  const [boardMode, setBoardMode] = useState('company');
  const [board, setBoard] = useState(null);
  const [stages, setStages] = useState([]);
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [expandedCard, setExpandedCard] = useState(null);
  const [showAddContact, setShowAddContact] = useState(null);
  const [showCompanyDetail, setShowCompanyDetail] = useState(null);
  const [contactForm, setContactForm] = useState({ name: '', job_title: '', email: '', linkedin_url: '', icp_tier: 1, notes: '' });
  const [dragData, setDragData] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [seeded, setSeeded] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(null);
  const [activityForm, setActivityForm] = useState({ type: 'Email Sent', description: '' });
  const [contactHistory, setContactHistory] = useState(null);
  const [settings, setSettings] = useState(null);
  const [settingsTab, setSettingsTab] = useState('apollo');
  const [apolloKeyInput, setApolloKeyInput] = useState('');
  const [apolloTestResult, setApolloTestResult] = useState(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [priorityWeights, setPriorityWeights] = useState(DEFAULT_PRIORITY_WEIGHTS);
  const [prioritySaveResult, setPrioritySaveResult] = useState(null);
  const [roleTitlesText, setRoleTitlesText] = useState(DEFAULT_APOLLO_ROLE_CONFIG.titles.join('\n'));
  const [roleSenioritiesText, setRoleSenioritiesText] = useState(DEFAULT_APOLLO_ROLE_CONFIG.seniorities.join('\n'));
  const [rolesSaveResult, setRolesSaveResult] = useState(null);
  const [apolloSearch, setApolloSearch] = useState(null);
  const [apolloResults, setApolloResults] = useState(null);
  const [apolloLoading, setApolloLoading] = useState(false);
  const [apolloEnriching, setApolloEnriching] = useState({});
  const [apolloAddError, setApolloAddError] = useState('');
  const [discoveryCatalog, setDiscoveryCatalog] = useState(null);
  const [targetCategories, setTargetCategories] = useState([]);
  const [discoverModalOpen, setDiscoverModalOpen] = useState(false);
  const [discoverLoading, setDiscoverLoading] = useState(false);
  const [discoverError, setDiscoverError] = useState(null);
  const [discoverResults, setDiscoverResults] = useState(null);
  const [discoverSummary, setDiscoverSummary] = useState(null);
  const [selectedDiscoverSet, setSelectedDiscoverSet] = useState(() => new Set());
  const boardRef = useRef(null);

  const fetchBoard = useCallback(() => {
    fetch(`${API}/api/kanban`).then(r => r.json()).then(data => {
      setBoard(data.board);
      setStages(data.stages);
    });
  }, []);

  const fetchStats = useCallback(() => {
    fetch(`${API}/api/stats`).then(r => r.json()).then(setStats);
  }, []);

  const fetchCompanies = useCallback(() => {
    fetch(`${API}/api/companies`).then(r => r.json()).then(setCompanies);
  }, []);

  const fetchSettings = useCallback(() => {
    fetch(`${API}/api/settings`).then(r => r.json()).then(setSettings);
  }, []);

  const fetchDiscoveryCatalog = useCallback(() => {
    fetch(`${API}/api/discovery/catalog`).then(r => r.json()).then(setDiscoveryCatalog);
  }, []);

  useEffect(() => { fetchBoard(); fetchStats(); fetchCompanies(); fetchSettings(); fetchDiscoveryCatalog(); }, [fetchBoard, fetchStats, fetchCompanies, fetchSettings, fetchDiscoveryCatalog]);

  useEffect(() => {
    if (!settings) return;
    if (settings.company_prioritisation_weights) {
      setPriorityWeights(settings.company_prioritisation_weights);
    }
    if (settings.apollo_role_config) {
      const titles = settings.apollo_role_config.titles || [];
      const seniorities = settings.apollo_role_config.seniorities || [];
      setRoleTitlesText(titles.join('\n'));
      setRoleSenioritiesText(seniorities.join('\n'));
    }
    if (Array.isArray(settings.discovery_target_categories) && settings.discovery_target_categories.length) {
      setTargetCategories(settings.discovery_target_categories);
    }
  }, [settings]);

  const [companyDetailOutreachDraft, setCompanyDetailOutreachDraft] = useState('');

  useEffect(() => {
    if (showCompanyDetail) {
      setCompanyDetailOutreachDraft(showCompanyDetail.outreach_notes || '');
    }
  }, [showCompanyDetail]);

  const toggleDiscoveryCategory = async (label) => {
    const next = targetCategories.includes(label)
      ? targetCategories.filter((l) => l !== label)
      : [...targetCategories, label];
    if (next.length === 0) return;
    setTargetCategories(next);
    await fetch(`${API}/api/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discovery_target_categories: next })
    });
    fetchSettings();
  };

  const runDiscover = async () => {
    setDiscoverLoading(true);
    setDiscoverError(null);
    setDiscoverResults(null);
    setDiscoverSummary(null);
    try {
      const res = await fetch(`${API}/api/discover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Discover failed');
      setDiscoverResults(data.results || []);
      setDiscoverSummary({
        new_count: data.new_count,
        total_candidates: data.total_candidates,
        already_in_crm: data.already_in_crm,
        categories_used: data.categories_used
      });
      setSelectedDiscoverSet(new Set((data.results || []).map((_, i) => i)));
    } catch (e) {
      setDiscoverError(e.message);
    }
    setDiscoverLoading(false);
  };

  const importDiscovered = async () => {
    if (!discoverResults?.length) return;
    const items = [...selectedDiscoverSet].sort((a, b) => a - b).map((i) => discoverResults[i]).filter(Boolean);
    if (!items.length) return;
    const res = await fetch(`${API}/api/companies/import-discovered`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    });
    const data = await res.json();
    if (!res.ok) {
      setDiscoverError(data.error || 'Import failed');
      return;
    }
    setDiscoverModalOpen(false);
    setDiscoverResults(null);
    setDiscoverSummary(null);
    fetchBoard();
    fetchCompanies();
    fetchStats();
  };

  const toggleDiscoverSelect = (idx) => {
    setSelectedDiscoverSet((prev) => {
      const n = new Set(prev);
      if (n.has(idx)) n.delete(idx);
      else n.add(idx);
      return n;
    });
  };

  const seedData = async () => {
    const { companies: seedCompanies } = await import('./seed-data');
    await fetch(`${API}/api/seed`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ companies: seedCompanies }) });
    setSeeded(true);
    fetchBoard(); fetchStats(); fetchCompanies();
  };

  const moveCompany = async (companyId, newStage) => {
    await fetch(`${API}/api/companies/${companyId}/stage`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stage: newStage })
    });
    fetchBoard(); fetchStats();
  };

  const saveCompanyOutreachNotes = useCallback(async (companyId, outreach_notes) => {
    const res = await fetch(`${API}/api/companies/${companyId}/outreach-notes`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ outreach_notes })
    });
    if (!res.ok) return;
    fetchBoard();
    fetchCompanies();
    setShowCompanyDetail((prev) => (prev && prev.id === companyId ? { ...prev, outreach_notes } : prev));
  }, [fetchBoard, fetchCompanies]);

  const [addContactError, setAddContactError] = useState('');

  const addContact = async (companyId) => {
    setAddContactError('');
    const res = await fetch(`${API}/api/contacts`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...contactForm, company_id: companyId })
    });
    let data = {};
    try {
      data = await res.json();
    } catch (e) {
      data = {};
    }
    if (!res.ok) {
      setAddContactError(data.error || `Could not add contact (${res.status})`);
      return;
    }
    setShowAddContact(null);
    setContactForm({ name: '', job_title: '', email: '', linkedin_url: '', icp_tier: 1, notes: '' });
    fetchBoard(); fetchStats();
  };

  const deleteContact = async (contactId) => {
    await fetch(`${API}/api/contacts/${contactId}`, { method: 'DELETE' });
    fetchBoard(); fetchStats();
  };

  const logActivity = async (contactId) => {
    await fetch(`${API}/api/contacts/${contactId}/activity`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ activity_type: activityForm.type, description: activityForm.description })
    });
    setShowActivityModal(null);
    setActivityForm({ type: 'Email Sent', description: '' });
    fetchBoard();
  };

  const updateContactStage = async (contactId, stage) => {
    await fetch(`${API}/api/contacts/${contactId}/stage`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stage })
    });
    fetchBoard();
  };

  const fetchHistory = async (contactId) => {
    const res = await fetch(`${API}/api/contacts/${contactId}/history`);
    const data = await res.json();
    setContactHistory({ contactId, entries: data });
  };

  const saveApolloKey = async () => {
    setSettingsSaving(true);
    setApolloTestResult(null);
    await fetch(`${API}/api/settings`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ apollo_api_key: apolloKeyInput })
    });
    setSettingsSaving(false);
    setApolloKeyInput('');
    fetchSettings();
  };

  const testApolloKey = async () => {
    setApolloTestResult({ loading: true });
    try {
      const res = await fetch(`${API}/api/settings/test-apollo`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) setApolloTestResult({ success: true, total: data.total });
      else setApolloTestResult({ success: false, error: data.error || 'Failed' });
    } catch (e) {
      setApolloTestResult({ success: false, error: e.message });
    }
  };

  const searchApollo = async (companyName, companyDomain, page = 1) => {
    setApolloLoading(true);
    try {
      const res = await fetch(`${API}/api/apollo/search`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: companyName,
          company_domain: companyDomain || null,
          page
        })
      });
      const data = await res.json();
      if (res.ok) setApolloResults(data);
      else setApolloResults({ error: data.error || 'Search failed' });
    } catch (e) {
      setApolloResults({ error: e.message });
    }
    setApolloLoading(false);
  };

  const parseLines = (value) => value
    .split('\n')
    .map(s => s.trim().toLowerCase())
    .filter(Boolean);

  const savePrioritySettings = async () => {
    setSettingsSaving(true);
    setPrioritySaveResult(null);
    try {
      const res = await fetch(`${API}/api/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_prioritisation_weights: priorityWeights })
      });
      if (!res.ok) throw new Error('Failed to save company prioritisation');
      setPrioritySaveResult({ success: true });
      fetchSettings();
      fetchBoard();
      fetchCompanies();
    } catch (e) {
      setPrioritySaveResult({ success: false, error: e.message });
    }
    setSettingsSaving(false);
  };

  const saveApolloRoleSettings = async () => {
    const titles = parseLines(roleTitlesText);
    const seniorities = parseLines(roleSenioritiesText);
    setSettingsSaving(true);
    setRolesSaveResult(null);
    try {
      const res = await fetch(`${API}/api/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apollo_role_config: { titles, seniorities } })
      });
      if (!res.ok) throw new Error('Failed to save Apollo role filters');
      setRolesSaveResult({ success: true, titles: titles.length, seniorities: seniorities.length });
      fetchSettings();
    } catch (e) {
      setRolesSaveResult({ success: false, error: e.message });
    }
    setSettingsSaving(false);
  };

  const enrichAndAddContact = async (person, companyId) => {
    setApolloAddError('');
    setApolloEnriching(prev => ({ ...prev, [person.apollo_id]: true }));
    try {
      const res = await fetch(`${API}/api/apollo/enrich`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          apollo_id: person.apollo_id,
          linkedin_url: person.linkedin_url,
          first_name: person.first_name,
          last_name: person.last_name,
          org_domain: person.org_domain,
        })
      });
      const enriched = await res.json();
      if (!res.ok) {
        setApolloAddError(enriched.error || 'Enrichment failed');
        return;
      }
      const email = enriched.email || '';
      const linkedin = enriched.linkedin_url || person.linkedin_url || '';
      const contactRes = await fetch(`${API}/api/contacts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_id: companyId,
          icp_tier: person.seniority === 'vp' || person.seniority === 'director' || person.seniority === 'head' ? 2 : person.seniority === 'senior' ? 3 : person.seniority === 'manager' ? 4 : 1,
          name: person.name,
          job_title: person.title || '',
          email,
          linkedin_url: linkedin,
          notes: `Added via Apollo${enriched.phone ? ' | Phone: ' + enriched.phone : ''}`,
        })
      });
      const contactData = await contactRes.json().catch(() => ({}));
      if (!contactRes.ok) {
        setApolloAddError(contactData.error || 'Could not add contact');
        return;
      }
      fetchBoard(); fetchStats();
    } catch (e) {
      console.error('Enrich failed:', e);
      setApolloAddError(e.message || 'Request failed');
    } finally {
      setApolloEnriching(prev => ({ ...prev, [person.apollo_id]: false }));
    }
  };

  const handleDragStartCompany = (e, company, fromStage) => {
    setDragData({ type: 'company', company, fromStage });
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.4';
  };

  const handleDragStartContact = (e, contact, fromStage) => {
    setDragData({ type: 'contact', contact, fromStage });
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.4';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDragData(null);
    setDragOverCol(null);
  };

  const handleDragOver = (e, stage) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCol(stage);
  };

  const handleDrop = (e, toStage) => {
    e.preventDefault();
    setDragOverCol(null);
    if (dragData && dragData.fromStage !== toStage) {
      if (dragData.type === 'company') {
        moveCompany(dragData.company.id, toStage);
      } else if (dragData.type === 'contact') {
        updateContactStage(dragData.contact.id, toStage);
      }
    }
    setDragData(null);
  };

  const totalCompanies = board ? Object.values(board).reduce((sum, arr) => sum + arr.length, 0) : 0;

  const filterCompanies = (list) => {
    let filtered = list;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(c => c.name.toLowerCase().includes(q) || c.category.toLowerCase().includes(q) || (c.speciality || '').toLowerCase().includes(q));
    }
    if (filterCategory !== 'all') {
      filtered = filtered.filter(c => c.category === filterCategory);
    }
    return filtered;
  };

  const getContactBoardMap = () => {
    if (!board) return {};
    const map = {};
    stages.forEach(stage => { map[stage] = []; });
    Object.entries(board).forEach(([companyStage, companyList]) => {
      companyList.forEach(company => {
        (company.contacts || []).forEach(ct => {
          const contactStage = ct.current_stage || 'Contact Identified';
          if (!map[contactStage]) map[contactStage] = [];
          map[contactStage].push({
            ...ct,
            company_id: company.id,
            company_name: company.name,
            company_category: company.category,
            company_stage: companyStage
          });
        });
      });
    });
    return map;
  };

  const filterContacts = (list) => {
    let filtered = list;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(ct =>
        (ct.name || '').toLowerCase().includes(q) ||
        (ct.job_title || '').toLowerCase().includes(q) ||
        (ct.email || '').toLowerCase().includes(q) ||
        (ct.company_name || '').toLowerCase().includes(q) ||
        (ct.company_category || '').toLowerCase().includes(q)
      );
    }
    if (filterCategory !== 'all') {
      filtered = filtered.filter(ct => ct.company_category === filterCategory);
    }
    return filtered;
  };

  const categories = board ? [...new Set(Object.values(board).flat().map(c => c.category))].sort() : [];

  // ─── KANBAN CARD ──────────────────────────────────────────────
  const KanbanCard = ({ company, stage }) => {
    const isExpanded = expandedCard === company.id;
    const catColor = CAT_COLORS[company.category] || '#6b7280';
    const [outreachDraft, setOutreachDraft] = useState(company.outreach_notes || '');
    useEffect(() => {
      setOutreachDraft(company.outreach_notes || '');
    }, [company.id, company.outreach_notes]);

    return (
      <div
        draggable
        onDragStart={(e) => handleDragStartCompany(e, company, stage)}
        onDragEnd={handleDragEnd}
        style={{
          background: SURFACE, borderRadius: 0, border: `1px solid ${BORDER}`,
          marginBottom: 6, cursor: 'grab', transition: 'border-color 0.15s',
          borderLeft: `3px solid ${catColor}`,
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = BORDER_HOVER; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.borderLeftColor = catColor; }}
      >
        <div style={{ padding: '10px 12px', cursor: 'pointer' }} onClick={() => setExpandedCard(isExpanded ? null : company.id)}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6 }}>
            <div style={{ fontWeight: 500, fontSize: 13, color: TEXT, lineHeight: 1.3 }}>{company.name}</div>
            <div style={{ fontSize: 10, fontWeight: 600, color: ACCENT, whiteSpace: 'nowrap', flexShrink: 0 }}>
              {company.priority_score}/25
            </div>
          </div>
          <div style={{ fontSize: 11, color: catColor, fontWeight: 400, marginTop: 3, opacity: 0.8 }}>{company.category}</div>
          <div style={{ display: 'flex', gap: 5, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            {company.contact_count > 0 && (
              <span style={{ fontSize: 10, background: 'rgba(255,255,255,0.06)', color: TEXT_MID, padding: '2px 6px', borderRadius: 0, fontWeight: 400 }}>
                {company.contact_count} contact{company.contact_count !== 1 ? 's' : ''}
              </span>
            )}
            {company.has_formal_programme ? (
              <span style={{ fontSize: 10, background: 'rgba(34,197,94,0.12)', color: '#4ade80', padding: '2px 6px', borderRadius: 0, fontWeight: 400 }}>
                Formal prog.
              </span>
            ) : null}
            {company.outreach_notes ? (
              <span style={{ fontSize: 10, background: 'rgba(250,22,59,0.12)', color: ACCENT, padding: '2px 6px', borderRadius: 0, fontWeight: 500 }}>
                Company notes
              </span>
            ) : null}
            <span style={{ marginLeft: 'auto', fontSize: 10, color: TEXT_DIM }}>{isExpanded ? '▾' : '▸'}</span>
          </div>
        </div>

        {isExpanded && (
          <div style={{ borderTop: `1px solid ${BORDER}`, padding: '10px 12px' }} onClick={e => e.stopPropagation()}>
            {company.speciality && (
              <p style={{ fontSize: 11, color: TEXT_DIM, marginBottom: 8, lineHeight: 1.4 }}>{company.speciality}</p>
            )}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: TEXT_MID, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
                Company outreach notes
              </div>
              <p style={{ fontSize: 11, color: TEXT_DIM, marginBottom: 6, lineHeight: 1.4 }}>
                Track company-level outreach (for example applying to their programme, not individual contact threads).
              </p>
              <textarea
                value={outreachDraft}
                onChange={(e) => setOutreachDraft(e.target.value)}
                placeholder="e.g. Applied via official work experience portal — deadline 15 May"
                style={{ ...inputStyle, minHeight: 72, resize: 'vertical', marginBottom: 6, fontSize: 12 }}
              />
              <button
                type="button"
                onClick={() => saveCompanyOutreachNotes(company.id, outreachDraft)}
                style={{ ...btnPrimary, padding: '6px 12px', fontSize: 11 }}
              >
                Save company notes
              </button>
            </div>
            <div style={{ display: 'flex', gap: 4, marginBottom: 10, flexWrap: 'wrap' }}>
              {company.linkedin_url && (
                <a href={company.linkedin_url} target="_blank" rel="noreferrer"
                  style={{ fontSize: 10, color: '#60a5fa', textDecoration: 'none', padding: '2px 7px', background: 'rgba(96,165,250,0.1)', borderRadius: 0, fontWeight: 400 }}>
                  LinkedIn
                </a>
              )}
              {company.internship_url && (
                <a href={company.internship_url} target="_blank" rel="noreferrer"
                  style={{ fontSize: 10, color: ACCENT, textDecoration: 'none', padding: '2px 7px', background: 'rgba(250,22,59,0.1)', borderRadius: 0, fontWeight: 400 }}>
                  Apply
                </a>
              )}
              <button onClick={() => setShowCompanyDetail(company)}
                style={{ fontSize: 10, color: TEXT_DIM, background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, borderRadius: 0, padding: '2px 7px', cursor: 'pointer', fontFamily: 'inherit' }}>
                Details
              </button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: TEXT_MID, textTransform: 'uppercase', letterSpacing: 1 }}>
                Contacts
              </span>
              <div style={{ display: 'flex', gap: 4 }}>
                {settings?.apollo_api_key_set && (
                  <button onClick={() => { setApolloSearch(company); setApolloResults(null); setApolloAddError(''); searchApollo(company.name); }}
                    style={{ fontSize: 10, color: '#fbbf24', background: 'rgba(251,191,36,0.1)', border: 'none', borderRadius: 0, padding: '3px 7px', cursor: 'pointer', fontWeight: 500, fontFamily: 'inherit' }}>
                    Apollo Lookup
                  </button>
                )}
                <button onClick={() => { setShowAddContact(company.id); setAddContactError(''); setContactForm({ name: '', job_title: '', email: '', linkedin_url: '', icp_tier: 1, notes: '' }); }}
                  style={{ fontSize: 10, color: '#fff', background: ACCENT, border: 'none', borderRadius: 0, padding: '3px 8px', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>
                  + Add
                </button>
              </div>
            </div>

            {(company.contacts || []).length === 0 && (
              <div style={{ fontSize: 11, color: TEXT_DIM, textAlign: 'center', padding: '8px 0' }}>
                No contacts yet
              </div>
            )}

            {(company.contacts || []).map(ct => (
              <div
                key={ct.id}
                draggable
                onDragStart={(e) => { e.stopPropagation(); handleDragStartContact(e, ct, ct.current_stage || 'Contact Identified'); }}
                onDragEnd={(e) => { e.stopPropagation(); handleDragEnd(e); }}
                style={{ padding: '7px 8px', background: 'rgba(255,255,255,0.03)', borderRadius: 0, marginBottom: 4, border: `1px solid ${BORDER}`, cursor: 'grab' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 4 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: TEXT }}>{ct.name || 'Unnamed'}</div>
                    {ct.job_title && <div style={{ fontSize: 10, color: TEXT_DIM, marginTop: 1 }}>{ct.job_title}</div>}
                  </div>
                  <span style={{
                    fontSize: 9, fontWeight: 500, padding: '2px 5px', borderRadius: 0, whiteSpace: 'nowrap', flexShrink: 0,
                    background: (STAGE_COLORS[ct.current_stage] || '#6b7280') + '20',
                    color: STAGE_COLORS[ct.current_stage] || '#6b7280'
                  }}>
                    {ct.current_stage || 'New'}
                  </span>
                </div>
                {ct.email && <div style={{ fontSize: 10, color: '#60a5fa', marginTop: 2 }}>{ct.email}</div>}
                <div style={{ fontSize: 9, color: TEXT_DIM, marginTop: 3 }}>
                  Drag contact card to update stage
                </div>
                <div style={{ display: 'flex', gap: 3, marginTop: 5, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 9, color: '#a78bfa', background: 'rgba(167,139,250,0.1)', padding: '1px 5px', borderRadius: 0 }}>
                    T{ct.icp_tier}
                  </span>
                  {ct.activity_count > 0 && (
                    <span style={{ fontSize: 9, color: TEXT_DIM, background: 'rgba(255,255,255,0.05)', padding: '1px 5px', borderRadius: 0 }}>
                      {ct.activity_count} act.
                    </span>
                  )}
                  {ct.linkedin_url && (
                    <a href={ct.linkedin_url} target="_blank" rel="noreferrer"
                      style={{ fontSize: 9, color: '#60a5fa', textDecoration: 'none', background: 'rgba(96,165,250,0.1)', padding: '1px 5px', borderRadius: 0 }}>
                      LI
                    </a>
                  )}
                  <button onClick={() => { setShowActivityModal(ct); setActivityForm({ type: 'Email Sent', description: '' }); }}
                    style={{ fontSize: 9, color: '#4ade80', background: 'rgba(74,222,128,0.1)', border: 'none', borderRadius: 0, padding: '1px 5px', cursor: 'pointer', fontFamily: 'inherit' }}>
                    + Activity
                  </button>
                  <button onClick={() => fetchHistory(ct.id)}
                    style={{ fontSize: 9, color: TEXT_DIM, background: 'rgba(255,255,255,0.05)', border: 'none', borderRadius: 0, padding: '1px 5px', cursor: 'pointer', fontFamily: 'inherit' }}>
                    History
                  </button>
                  <button onClick={() => { if (window.confirm('Delete this contact?')) deleteContact(ct.id); }}
                    style={{ fontSize: 9, color: '#f87171', background: 'rgba(248,113,113,0.1)', border: 'none', borderRadius: 0, padding: '1px 5px', cursor: 'pointer', fontFamily: 'inherit' }}>
                    Del
                  </button>
                </div>
              </div>
            ))}

            <div style={{ marginTop: 8, display: 'flex', gap: 4, alignItems: 'center' }}>
              <span style={{ fontSize: 10, fontWeight: 500, color: TEXT_DIM }}>Move:</span>
              <select
                value={stage}
                onChange={e => moveCompany(company.id, e.target.value)}
                style={{ fontSize: 10, padding: '3px 4px', borderRadius: 0, border: `1px solid ${BORDER}`, background: SURFACE_2, color: TEXT_MID, cursor: 'pointer', flex: 1, minWidth: 80, fontFamily: 'inherit' }}
              >
                {stages.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>
    );
  };

  const ContactKanbanCard = ({ contact, stage }) => {
    const stageColor = STAGE_COLORS[stage] || '#6b7280';
    const catColor = CAT_COLORS[contact.company_category] || '#6b7280';
    return (
      <div
        draggable
        onDragStart={(e) => handleDragStartContact(e, contact, stage)}
        onDragEnd={handleDragEnd}
        style={{
          background: SURFACE,
          borderRadius: 0,
          border: `1px solid ${BORDER}`,
          marginBottom: 6,
          cursor: 'grab',
          transition: 'border-color 0.15s',
          borderLeft: `3px solid ${stageColor}`,
          padding: '9px 10px'
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = BORDER_HOVER; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.borderLeftColor = stageColor; }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: TEXT }}>{contact.name || 'Unnamed'}</div>
            {contact.job_title && <div style={{ fontSize: 10, color: TEXT_DIM, marginTop: 2 }}>{contact.job_title}</div>}
          </div>
          <span style={{ fontSize: 9, color: TEXT_DIM, background: 'rgba(255,255,255,0.05)', padding: '1px 6px', borderRadius: 0 }}>
            T{contact.icp_tier}
          </span>
        </div>
        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 9, color: catColor, background: catColor + '18', padding: '1px 6px', borderRadius: 0 }}>
            {contact.company_category}
          </span>
          <span style={{ fontSize: 10, color: TEXT_MID }}>{contact.company_name}</span>
        </div>
        {contact.email && <div style={{ fontSize: 10, color: '#60a5fa', marginTop: 4 }}>{contact.email}</div>}
        <div style={{ marginTop: 5, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {contact.linkedin_url && (
            <a
              href={contact.linkedin_url}
              target="_blank"
              rel="noreferrer"
              onClick={e => e.stopPropagation()}
              style={{ fontSize: 9, color: '#60a5fa', textDecoration: 'none', background: 'rgba(96,165,250,0.1)', padding: '1px 6px', borderRadius: 0 }}
            >
              LinkedIn
            </a>
          )}
          <button
            onClick={() => { setShowActivityModal(contact); setActivityForm({ type: 'Email Sent', description: '' }); }}
            style={{ fontSize: 9, color: '#4ade80', background: 'rgba(74,222,128,0.1)', border: 'none', borderRadius: 0, padding: '1px 6px', cursor: 'pointer', fontFamily: 'inherit' }}
          >
            + Activity
          </button>
          <button
            onClick={() => fetchHistory(contact.id)}
            style={{ fontSize: 9, color: TEXT_DIM, background: 'rgba(255,255,255,0.05)', border: 'none', borderRadius: 0, padding: '1px 6px', cursor: 'pointer', fontFamily: 'inherit' }}
          >
            History
          </button>
        </div>
      </div>
    );
  };

  // ─── KANBAN BOARD VIEW ────────────────────────────────────────
  const KanbanBoard = () => {
    if (!board) return <div style={{ textAlign: 'center', padding: 60, color: TEXT_DIM }}>Loading...</div>;
    const contactBoard = getContactBoardMap();
    const totalContactsOnBoard = Object.values(contactBoard).reduce((sum, arr) => sum + arr.length, 0);

    if (totalCompanies === 0 && !seeded) {
      return (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ background: SURFACE, borderRadius: 0, border: `1px solid ${BORDER}`, padding: 48, maxWidth: 480, margin: '0 auto' }}>
            <h2 style={{ marginBottom: 8, fontSize: 20, fontWeight: 600, color: TEXT }}>Welcome to the Work Experience & Internship Outreach Tracker</h2>
            <p style={{ color: TEXT_DIM, marginBottom: 24, fontSize: 14, fontWeight: 300 }}>Load all 38 target firms to populate the pipeline board.</p>
            <button onClick={seedData} style={btnPrimary}>
              Load Target Companies
            </button>
          </div>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 56px)' }}>
        <div style={{ display: 'flex', gap: 8, padding: '12px 0', flexWrap: 'wrap', alignItems: 'center', flexShrink: 0 }}>
          <input
            type="text" placeholder="Search..." value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ ...inputStyle, width: 180, marginBottom: 0, background: SURFACE, border: `1px solid ${BORDER}`, color: TEXT }}
          />
          <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
            style={{ ...selectStyle, width: 'auto', marginBottom: 0, background: SURFACE, border: `1px solid ${BORDER}`, color: TEXT_MID }}>
            <option value="all">All categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <div style={{ display: 'flex', gap: 4, padding: 2, borderRadius: 0, border: `1px solid ${BORDER}`, background: SURFACE }}>
            <button
              onClick={() => setBoardMode('company')}
              style={{
                fontSize: 11, fontWeight: 500, padding: '5px 9px', borderRadius: 0, border: 'none',
                background: boardMode === 'company' ? ACCENT : 'transparent',
                color: boardMode === 'company' ? '#fff' : TEXT_DIM, cursor: 'pointer', fontFamily: 'inherit'
              }}
            >
              Company view
            </button>
            <button
              onClick={() => setBoardMode('contact')}
              style={{
                fontSize: 11, fontWeight: 500, padding: '5px 9px', borderRadius: 0, border: 'none',
                background: boardMode === 'contact' ? ACCENT : 'transparent',
                color: boardMode === 'contact' ? '#fff' : TEXT_DIM, cursor: 'pointer', fontFamily: 'inherit'
              }}
            >
              Contact view
            </button>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 16, fontSize: 12, color: TEXT_DIM }}>
            <span><strong style={{ color: TEXT, fontWeight: 500 }}>{totalCompanies}</strong> companies</span>
            {stats && <span><strong style={{ color: TEXT, fontWeight: 500 }}>{boardMode === 'company' ? stats.total_contacts : totalContactsOnBoard}</strong> contacts</span>}
            {stats && <span><strong style={{ color: TEXT, fontWeight: 500 }}>{stats.total_activities}</strong> activities</span>}
          </div>
        </div>

        <div ref={boardRef} style={{
          display: 'flex', gap: 8, overflowX: 'auto', flex: 1, paddingBottom: 8, alignItems: 'flex-start'
        }}>
          {stages.map(stage => {
            const stageCompanies = filterCompanies(board[stage] || []);
            const stageContacts = filterContacts(contactBoard[stage] || []);
            const total = boardMode === 'company' ? (board[stage] || []).length : (contactBoard[stage] || []).length;
            const color = STAGE_COLORS[stage] || '#6b7280';
            const isDragOver = dragOverCol === stage;

            return (
              <div
                key={stage}
                onDragOver={(e) => handleDragOver(e, stage)}
                onDragLeave={() => setDragOverCol(null)}
                onDrop={(e) => handleDrop(e, stage)}
                style={{
                  minWidth: 312, maxWidth: 312, flexShrink: 0,
                  background: isDragOver ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.02)',
                  borderRadius: 0, display: 'flex', flexDirection: 'column',
                  maxHeight: '100%',
                  border: isDragOver ? `1px dashed ${color}` : `1px solid transparent`,
                  transition: 'background 0.15s, border 0.15s',
                }}
              >
                <div style={{
                  padding: '10px 10px', borderBottom: `2px solid ${color}30`,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  position: 'sticky', top: 0, zIndex: 1,
                  background: isDragOver ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.02)',
                  borderRadius: 0,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: color }} />
                    <span style={{ fontSize: 11, fontWeight: 500, color: TEXT }}>{stage}</span>
                  </div>
                  <span style={{
                    fontSize: 10, fontWeight: 600, color,
                    background: color + '15', padding: '1px 7px', borderRadius: 0
                  }}>
                    {total}
                  </span>
                </div>

                <div style={{ padding: '6px 6px', overflowY: 'auto', flex: 1 }}>
                  {boardMode === 'company' && stageCompanies.map(company => (
                    <KanbanCard key={company.id} company={company} stage={stage} />
                  ))}
                  {boardMode === 'contact' && stageContacts.map(contact => (
                    <ContactKanbanCard key={contact.id} contact={contact} stage={stage} />
                  ))}
                  {boardMode === 'company' && stageCompanies.length === 0 && total === 0 && (
                    <div style={{ textAlign: 'center', padding: '24px 8px', color: 'rgba(255,255,255,0.12)', fontSize: 12 }}>
                      Drop here
                    </div>
                  )}
                  {boardMode === 'contact' && stageContacts.length === 0 && total === 0 && (
                    <div style={{ textAlign: 'center', padding: '24px 8px', color: 'rgba(255,255,255,0.12)', fontSize: 12 }}>
                      Drop here
                    </div>
                  )}
                  {boardMode === 'company' && stageCompanies.length === 0 && total > 0 && (
                    <div style={{ textAlign: 'center', padding: '12px 8px', color: TEXT_DIM, fontSize: 11 }}>
                      {total} hidden by filters
                    </div>
                  )}
                  {boardMode === 'contact' && stageContacts.length === 0 && total > 0 && (
                    <div style={{ textAlign: 'center', padding: '12px 8px', color: TEXT_DIM, fontSize: 11 }}>
                      {total} hidden by filters
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <Footer />
      </div>
    );
  };

  // ─── COMPANY LIST VIEW ────────────────────────────────────────
  const CompanyList = () => (
    <div>
      <p style={{ fontSize: 13, color: TEXT_DIM, marginBottom: 14, lineHeight: 1.5 }}>
        Browse and manage your CRM list. Use Discover to suggest firms from the reference catalogue that match your targeting categories (configured in Settings, Companies tab) and are not already in your list.
      </p>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, color: TEXT }}>All Companies ({companies.length})</h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => { setDiscoverModalOpen(true); setDiscoverError(null); setDiscoverResults(null); setDiscoverSummary(null); }}
            style={{ ...btnPrimary, fontSize: 12, padding: '8px 16px', textDecoration: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
          >
            Discover companies
          </button>
          <a href={`${API}/api/export`} style={{
            ...btnSecondary, textDecoration: 'none', display: 'inline-block', fontSize: 12, padding: '8px 14px'
          }}>Export CSV</a>
        </div>
      </div>
      {companies.map((c, i) => (
        <div key={c.id} style={{
          background: SURFACE, borderRadius: 0, border: `1px solid ${BORDER}`, padding: 14, marginBottom: 8,
          cursor: 'pointer', display: 'flex', gap: 12, alignItems: 'flex-start', transition: 'border-color 0.15s'
        }} onClick={() => setShowCompanyDetail(c)}
           onMouseEnter={e => { e.currentTarget.style.borderColor = BORDER_HOVER; }}
           onMouseLeave={e => { e.currentTarget.style.borderColor = BORDER; }}>
          <div style={{
            minWidth: 30, width: 30, height: 30, borderRadius: 0,
            background: CAT_COLORS[c.category] || '#6b7280',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#000', fontSize: 12, fontWeight: 600, flexShrink: 0
          }}>{i + 1}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 14, fontWeight: 500, color: TEXT }}>{c.name}</span>
              <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 0, background: (CAT_COLORS[c.category] || '#6b7280') + '18', color: CAT_COLORS[c.category] || '#6b7280' }}>{c.category}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: ACCENT }}>{c.priority_score}/25</span>
            </div>
            <p style={{ fontSize: 12, color: TEXT_DIM, marginTop: 3 }}>{c.speciality}</p>
            <div style={{ display: 'flex', gap: 8, marginTop: 5, fontSize: 11, color: TEXT_DIM, alignItems: 'center' }}>
              <span>{c.contact_count || 0} contacts</span>
              <span style={{
                fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 0,
                background: (STAGE_COLORS[c.stage || c.latest_stage || 'Scouted'] || '#6b7280') + '18',
                color: STAGE_COLORS[c.stage || c.latest_stage || 'Scouted'] || '#6b7280'
              }}>{c.stage || c.latest_stage || 'Scouted'}</span>
            </div>
          </div>
        </div>
      ))}
      <Footer />
    </div>
  );

  // ─── ADD CONTACT MODAL ──────────────────────────────────────
  const AddContactModal = () => {
    if (!showAddContact) return null;
    const companyName = board ? Object.values(board).flat().find(c => c.id === showAddContact)?.name : '';
    return (
      <div style={modalOverlay} onClick={() => { setShowAddContact(null); setAddContactError(''); }}>
        <div style={modalBox} onClick={e => e.stopPropagation()}>
          <h3 style={{ marginBottom: 4, fontSize: 16, fontWeight: 600, color: TEXT }}>Add Contact</h3>
          <p style={{ fontSize: 13, color: TEXT_DIM, marginBottom: 16 }}>{companyName}</p>
          {addContactError && (
            <div style={{ padding: 10, marginBottom: 12, background: 'rgba(248,113,113,0.12)', border: `1px solid rgba(248,113,113,0.35)`, color: '#f87171', fontSize: 13 }}>
              {addContactError}
            </div>
          )}
          <input placeholder="Full name" value={contactForm.name} onChange={e => setContactForm({ ...contactForm, name: e.target.value })} style={inputStyle} />
          <input placeholder="Job title" value={contactForm.job_title} onChange={e => setContactForm({ ...contactForm, job_title: e.target.value })} style={inputStyle} />
          <input placeholder="Email address" value={contactForm.email} onChange={e => setContactForm({ ...contactForm, email: e.target.value })} style={inputStyle} />
          <input placeholder="LinkedIn URL" value={contactForm.linkedin_url} onChange={e => setContactForm({ ...contactForm, linkedin_url: e.target.value })} style={inputStyle} />
          <select value={contactForm.icp_tier} onChange={e => setContactForm({ ...contactForm, icp_tier: parseInt(e.target.value) })} style={selectStyle}>
            {[1,2,3,4].map(t => <option key={t} value={t}>Tier {t}: {TIER_LABELS[t]}</option>)}
          </select>
          <textarea placeholder="Notes" value={contactForm.notes} onChange={e => setContactForm({ ...contactForm, notes: e.target.value })} style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button onClick={() => addContact(showAddContact)} style={btnPrimary}>Add Contact</button>
            <button onClick={() => { setShowAddContact(null); setAddContactError(''); }} style={btnSecondary}>Cancel</button>
          </div>
        </div>
      </div>
    );
  };

  // ─── ACTIVITY MODAL ─────────────────────────────────────────
  const ActivityModal = () => {
    if (!showActivityModal) return null;
    return (
      <div style={modalOverlay} onClick={() => setShowActivityModal(null)}>
        <div style={modalBox} onClick={e => e.stopPropagation()}>
          <h3 style={{ marginBottom: 4, fontSize: 16, fontWeight: 600, color: TEXT }}>Log Activity</h3>
          <p style={{ fontSize: 13, color: TEXT_DIM, marginBottom: 12 }}>{showActivityModal.name || 'Contact'}</p>
          <select value={activityForm.type} onChange={e => setActivityForm({ ...activityForm, type: e.target.value })} style={selectStyle}>
            {['Email Sent','Email Received','LinkedIn Message','LinkedIn Connection','Phone Call','Meeting','Note'].map(t =>
              <option key={t} value={t}>{t}</option>
            )}
          </select>
          <textarea placeholder="Description" value={activityForm.description} onChange={e => setActivityForm({ ...activityForm, description: e.target.value })} style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }} />
          <div style={{ marginTop: 4, marginBottom: 12 }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: TEXT_MID, textTransform: 'uppercase', letterSpacing: 1 }}>Update contact stage</span>
            <select
              defaultValue={showActivityModal.current_stage || 'Contact Identified'}
              onChange={e => updateContactStage(showActivityModal.id, e.target.value)}
              style={{ ...selectStyle, marginTop: 4 }}
            >
              {stages.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => logActivity(showActivityModal.id)} style={btnPrimary}>Log Activity</button>
            <button onClick={() => setShowActivityModal(null)} style={btnSecondary}>Cancel</button>
          </div>
        </div>
      </div>
    );
  };

  // ─── HISTORY MODAL ──────────────────────────────────────────
  const HistoryModal = () => {
    if (!contactHistory) return null;
    return (
      <div style={modalOverlay} onClick={() => setContactHistory(null)}>
        <div style={modalBox} onClick={e => e.stopPropagation()}>
          <h3 style={{ marginBottom: 12, fontSize: 16, fontWeight: 600, color: TEXT }}>Stage History</h3>
          {contactHistory.entries.length === 0 && <p style={{ color: TEXT_DIM, fontSize: 13 }}>No history yet.</p>}
          {contactHistory.entries.map((entry, i) => (
            <div key={entry.id} style={{ padding: '8px 0', borderBottom: i < contactHistory.entries.length - 1 ? `1px solid ${BORDER}` : 'none', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: STAGE_COLORS[entry.stage] || '#6b7280', marginTop: 5, flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: TEXT }}>{entry.stage}</div>
                {entry.notes && <div style={{ fontSize: 11, color: TEXT_DIM, marginTop: 2 }}>{entry.notes}</div>}
                <div style={{ fontSize: 10, color: TEXT_DIM, marginTop: 2 }}>{new Date(entry.created_at).toLocaleString()}</div>
              </div>
            </div>
          ))}
          <button onClick={() => setContactHistory(null)} style={{ ...btnSecondary, marginTop: 12 }}>Close</button>
        </div>
      </div>
    );
  };

  // ─── COMPANY DETAIL MODAL ───────────────────────────────────
  const CompanyDetailModal = () => {
    if (!showCompanyDetail) return null;
    const c = showCompanyDetail;
    return (
      <div style={modalOverlay} onClick={() => setShowCompanyDetail(null)}>
        <div style={{ ...modalBox, maxWidth: 560 }} onClick={e => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
            <div>
              <h3 style={{ fontSize: 18, fontWeight: 600, color: TEXT }}>{c.name}</h3>
              <span style={{ fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 0, background: (CAT_COLORS[c.category] || '#6b7280') + '18', color: CAT_COLORS[c.category] || '#6b7280' }}>{c.category}</span>
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: ACCENT }}>{c.priority_score}/25</div>
          </div>
          <p style={{ fontSize: 13, color: TEXT_DIM, lineHeight: 1.6, marginBottom: 12, fontWeight: 300 }}>{c.description}</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: TEXT_DIM, textTransform: 'uppercase', letterSpacing: 1 }}>Divisions</div>
              <div style={{ fontSize: 12, marginTop: 3, color: TEXT_MID }}>{c.divisions}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: TEXT_DIM, textTransform: 'uppercase', letterSpacing: 1 }}>Speciality</div>
              <div style={{ fontSize: 12, marginTop: 3, color: TEXT_MID }}>{c.speciality}</div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6, marginBottom: 12 }}>
            {[['Access', c.score_accessibility], ['Relevance', c.score_relevance], ['Scale', c.score_scale], ['Comp.', c.score_competition], ['Warm', c.score_warm_path]].map(([label, val]) => (
              <div key={label} style={{ textAlign: 'center', padding: 6, background: 'rgba(255,255,255,0.03)', borderRadius: 0, border: `1px solid ${BORDER}` }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: TEXT }}>{val}</div>
                <div style={{ fontSize: 9, color: TEXT_DIM, marginTop: 2 }}>{label}</div>
              </div>
            ))}
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: TEXT_DIM, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Company outreach notes</div>
            <p style={{ fontSize: 11, color: TEXT_DIM, marginBottom: 6 }}>Use this for company-level tracking (programme applications, firm-wide outreach), separate from individual contacts.</p>
            <textarea
              value={companyDetailOutreachDraft}
              onChange={(e) => setCompanyDetailOutreachDraft(e.target.value)}
              style={{ ...inputStyle, minHeight: 80, resize: 'vertical', marginBottom: 8, fontSize: 12 }}
            />
            <button
              type="button"
              onClick={() => saveCompanyOutreachNotes(c.id, companyDetailOutreachDraft)}
              style={{ ...btnPrimary, padding: '6px 12px', fontSize: 12 }}
            >
              Save company notes
            </button>
          </div>
          {c.notes && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: TEXT_DIM, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Research notes</div>
              <p style={{ fontSize: 12, color: TEXT_DIM, lineHeight: 1.5, fontWeight: 400 }}>{c.notes}</p>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {c.linkedin_url && <a href={c.linkedin_url} target="_blank" rel="noreferrer" style={{ ...btnSecondary, textDecoration: 'none', fontSize: 12, padding: '6px 12px' }}>LinkedIn</a>}
            {c.internship_url && <a href={c.internship_url} target="_blank" rel="noreferrer" style={{ ...btnPrimary, textDecoration: 'none', fontSize: 12, padding: '6px 12px' }}>Apply</a>}
            <button onClick={() => setShowCompanyDetail(null)} style={{ ...btnSecondary, fontSize: 12, padding: '6px 12px' }}>Close</button>
          </div>
        </div>
      </div>
    );
  };

  // ─── SETTINGS VIEW ──────────────────────────────────────────
  const SettingsView = () => (
    <div style={{ maxWidth: 760, margin: '0 auto', paddingTop: 16 }}>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: TEXT, marginBottom: 20 }}>Settings</h2>

      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { id: 'apollo', label: 'Apollo' },
          { id: 'prioritisation', label: 'Company Prioritisation' },
          { id: 'roles', label: 'Apollo Roles' },
          { id: 'companies', label: 'Companies' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setSettingsTab(tab.id)}
            style={{
              fontSize: 12,
              fontWeight: 600,
              padding: '8px 12px',
              borderRadius: 0,
              border: settingsTab === tab.id ? `1px solid ${ACCENT}` : `1px solid ${BORDER}`,
              background: settingsTab === tab.id ? ACCENT : SURFACE,
              color: '#fff',
              cursor: 'pointer',
              fontFamily: 'inherit'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {settingsTab === 'apollo' && (
        <>
          <div style={{ background: SURFACE, borderRadius: 0, border: `1px solid ${BORDER}`, padding: 24, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: settings?.apollo_api_key_set ? '#4ade80' : '#525252' }} />
              <h3 style={{ fontSize: 15, fontWeight: 600, color: TEXT }}>Apollo.io Integration</h3>
            </div>
            <p style={{ fontSize: 13, color: TEXT_DIM, marginBottom: 16, lineHeight: 1.5 }}>
              Connect your Apollo.io API key to search for contacts at target companies and import them directly into your pipeline.
            </p>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: TEXT_MID, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Status</div>
              {settings?.apollo_api_key_set ? (
                <div style={{ fontSize: 13, color: '#4ade80' }}>
                  Connected <span style={{ color: TEXT_DIM, marginLeft: 8 }}>{settings.apollo_api_key_masked}</span>
                </div>
              ) : (
                <div style={{ fontSize: 13, color: TEXT_DIM }}>Not configured</div>
              )}
            </div>

            <div style={{ fontSize: 10, fontWeight: 600, color: TEXT_MID, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
              {settings?.apollo_api_key_set ? 'Update' : 'Enter'} API Key
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="password"
                placeholder="Paste your Apollo master API key"
                value={apolloKeyInput}
                onChange={e => setApolloKeyInput(e.target.value)}
                style={{ flex: 1, padding: '10px 12px', borderRadius: 0, border: `1px solid ${BORDER}`, fontSize: 13, fontFamily: 'inherit', fontWeight: 300, background: BG, color: TEXT, outline: 'none' }}
              />
              <button onClick={saveApolloKey} disabled={!apolloKeyInput || settingsSaving}
                style={{ ...btnPrimaryStyle, opacity: (!apolloKeyInput || settingsSaving) ? 0.5 : 1, padding: '10px 16px', whiteSpace: 'nowrap' }}>
                {settingsSaving ? 'Saving...' : 'Save Key'}
              </button>
            </div>
            <p style={{ fontSize: 11, color: TEXT_DIM, marginTop: 8 }}>
              Get your key from <a href="https://app.apollo.io/#/settings/integrations/api-keys" target="_blank" rel="noreferrer" style={{ color: '#60a5fa' }}>Apollo Settings &gt; API Keys</a>. Use a Master key for full access.
            </p>

            {settings?.apollo_api_key_set && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${BORDER}` }}>
                <button onClick={testApolloKey} style={{ ...btnSecondaryStyle, padding: '8px 14px', fontSize: 12 }}>
                  Test Connection
                </button>
                {apolloTestResult && !apolloTestResult.loading && (
                  <span style={{ marginLeft: 12, fontSize: 12, color: apolloTestResult.success ? '#4ade80' : '#f87171' }}>
                    {apolloTestResult.success ? `Connected (${apolloTestResult.total.toLocaleString()} records accessible)` : `Failed: ${apolloTestResult.error}`}
                  </span>
                )}
                {apolloTestResult?.loading && (
                  <span style={{ marginLeft: 12, fontSize: 12, color: TEXT_DIM }}>Testing...</span>
                )}
              </div>
            )}

            {settings?.apollo_api_key_set && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${BORDER}` }}>
                <button
                  onClick={async () => {
                    await fetch(`${API}/api/settings`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ apollo_api_key: '' }) });
                    fetchSettings();
                    setApolloTestResult(null);
                  }}
                  style={{ fontSize: 12, color: '#f87171', background: 'rgba(248,113,113,0.1)', border: 'none', borderRadius: 0, padding: '6px 12px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 }}>
                  Remove API Key
                </button>
              </div>
            )}
          </div>

          <div style={{ background: SURFACE, borderRadius: 0, border: `1px solid ${BORDER}`, padding: 24 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: TEXT, marginBottom: 12 }}>How it works</h3>
            <div style={{ fontSize: 13, color: TEXT_DIM, lineHeight: 1.6 }}>
              <p style={{ marginBottom: 8 }}><strong style={{ color: TEXT_MID, fontWeight: 500 }}>1. Search</strong> Open any company card on the board and click the "Apollo Lookup" button. This searches Apollo's database for people at that company matching the configured role filters.</p>
              <p style={{ marginBottom: 8 }}><strong style={{ color: TEXT_MID, fontWeight: 500 }}>2. Add</strong> Click "Add" next to any person in the results. The app enriches their profile through Apollo to pull their verified email address, then creates a new contact in your pipeline.</p>
              <p><strong style={{ color: TEXT_MID, fontWeight: 500 }}>3. Track</strong> The contact is automatically set to "Contact Identified" stage. Log activities, update stages, and move companies through your pipeline as normal.</p>
            </div>
          </div>
        </>
      )}

      {settingsTab === 'prioritisation' && (
        <div style={{ background: SURFACE, borderRadius: 0, border: `1px solid ${BORDER}`, padding: 24, marginBottom: 16 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: TEXT, marginBottom: 8 }}>Company Prioritisation Criteria</h3>
          <p style={{ fontSize: 13, color: TEXT_DIM, marginBottom: 14 }}>
            Set weighting for each scoring criterion. Priority scores are recalculated immediately for all companies.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              ['accessibility', 'Accessibility'],
              ['relevance', 'Relevance'],
              ['scale', 'Scale'],
              ['competition', 'Competition'],
              ['warm_path', 'Warm Path']
            ].map(([key, label]) => (
              <label key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 11, color: TEXT_MID }}>{label} weight</span>
                <input
                  type="number"
                  min="0"
                  max="10"
                  step="0.1"
                  value={priorityWeights[key]}
                  onChange={(e) => setPriorityWeights(prev => ({ ...prev, [key]: Number(e.target.value) }))}
                  style={{ ...inputStyle, marginBottom: 0 }}
                />
              </label>
            ))}
          </div>
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={savePrioritySettings} style={btnPrimaryStyle} disabled={settingsSaving}>
              {settingsSaving ? 'Saving...' : 'Save Prioritisation'}
            </button>
            {prioritySaveResult?.success && <span style={{ fontSize: 12, color: '#4ade80' }}>Saved and recalculated.</span>}
            {prioritySaveResult && !prioritySaveResult.success && <span style={{ fontSize: 12, color: '#f87171' }}>{prioritySaveResult.error}</span>}
          </div>
        </div>
      )}

      {settingsTab === 'roles' && (
        <div style={{ background: SURFACE, borderRadius: 0, border: `1px solid ${BORDER}`, padding: 24, marginBottom: 16 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: TEXT, marginBottom: 8 }}>Apollo Role Filters</h3>
          <p style={{ fontSize: 13, color: TEXT_DIM, marginBottom: 14 }}>
            Define exactly which roles Apollo should retrieve. Results are filtered to these roles and ranked by this order.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <div style={{ fontSize: 11, color: TEXT_MID, marginBottom: 4 }}>Job Titles (one per line)</div>
              <textarea
                value={roleTitlesText}
                onChange={(e) => setRoleTitlesText(e.target.value)}
                style={{ ...inputStyle, minHeight: 180, resize: 'vertical', marginBottom: 0 }}
              />
            </div>
            <div>
              <div style={{ fontSize: 11, color: TEXT_MID, marginBottom: 4 }}>Seniority (one per line)</div>
              <textarea
                value={roleSenioritiesText}
                onChange={(e) => setRoleSenioritiesText(e.target.value)}
                style={{ ...inputStyle, minHeight: 180, resize: 'vertical', marginBottom: 0 }}
              />
            </div>
          </div>
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={saveApolloRoleSettings} style={btnPrimaryStyle} disabled={settingsSaving}>
              {settingsSaving ? 'Saving...' : 'Save Role Filters'}
            </button>
            {rolesSaveResult?.success && (
              <span style={{ fontSize: 12, color: '#4ade80' }}>
                Saved ({rolesSaveResult.titles} titles, {rolesSaveResult.seniorities} seniorities).
              </span>
            )}
            {rolesSaveResult && !rolesSaveResult.success && <span style={{ fontSize: 12, color: '#f87171' }}>{rolesSaveResult.error}</span>}
          </div>
        </div>
      )}

      {settingsTab === 'companies' && (
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: TEXT, marginBottom: 8 }}>Discovery targeting</h3>
          <p style={{ fontSize: 13, color: TEXT_DIM, marginBottom: 16, lineHeight: 1.55 }}>
            Choose which company categories to include when you run Discover on the Companies page. At least one category must stay selected. Firms are matched from the built-in reference catalogue (same source as Load Target Companies).
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
            {(discoveryCatalog?.categories || []).map((cat) => {
              const on = targetCategories.includes(cat.label);
              return (
                <div
                  key={cat.label}
                  style={{
                    border: `1px solid ${on ? ACCENT : BORDER}`,
                    background: on ? 'rgba(250,22,59,0.06)' : SURFACE,
                    padding: 14,
                    borderRadius: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    minHeight: 180
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: TEXT }}>{cat.shortLabel}</div>
                      <div style={{ fontSize: 10, color: TEXT_DIM, marginTop: 4 }}>{cat.prospectCount} firms in catalogue</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleDiscoveryCategory(cat.label)}
                      disabled={on && targetCategories.length <= 1}
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        padding: '4px 10px',
                        borderRadius: 0,
                        border: `1px solid ${on ? ACCENT : BORDER}`,
                        background: on ? ACCENT : 'transparent',
                        color: '#fff',
                        cursor: on && targetCategories.length <= 1 ? 'not-allowed' : 'pointer',
                        opacity: on && targetCategories.length <= 1 ? 0.5 : 1,
                        fontFamily: 'inherit',
                        flexShrink: 0
                      }}
                    >
                      {on ? 'Targeting' : 'Include'}
                    </button>
                  </div>
                  <p style={{ fontSize: 11, color: TEXT_MID, lineHeight: 1.45, flex: 1 }}>{cat.description}</p>
                  <p style={{ fontSize: 10, color: TEXT_DIM, lineHeight: 1.4, fontStyle: 'italic' }}>{cat.rationale}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );

  // ─── APOLLO SEARCH MODAL ──────────────────────────────────
  const ApolloSearchModal = () => {
    if (!apolloSearch) return null;
    const company = apolloSearch;

    return (
      <div style={modalOverlay} onClick={() => { setApolloSearch(null); setApolloResults(null); setApolloAddError(''); }}>
        <div style={{ ...modalBox, maxWidth: 640 }} onClick={e => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: TEXT }}>Apollo People Search</h3>
              <p style={{ fontSize: 13, color: TEXT_DIM, marginTop: 2 }}>{company.name}</p>
            </div>
            <button onClick={() => { setApolloSearch(null); setApolloResults(null); setApolloAddError(''); }}
              style={{ background: 'none', border: 'none', color: TEXT_DIM, fontSize: 18, cursor: 'pointer', padding: '0 4px' }}>x</button>
          </div>

          {apolloAddError && (
            <div style={{ padding: 12, marginBottom: 12, background: 'rgba(248,113,113,0.12)', border: `1px solid rgba(248,113,113,0.35)`, color: '#f87171', fontSize: 13 }}>
              {apolloAddError}
            </div>
          )}

          {apolloLoading && (
            <div style={{ textAlign: 'center', padding: 32, color: TEXT_DIM, fontSize: 13 }}>
              Searching Apollo...
            </div>
          )}

          {apolloResults?.error && (
            <div style={{ padding: 16, background: 'rgba(248,113,113,0.1)', borderRadius: 0, color: '#f87171', fontSize: 13, marginBottom: 12 }}>
              {apolloResults.error}
            </div>
          )}

          {apolloResults && !apolloResults.error && !apolloLoading && (
            <>
              <div style={{ fontSize: 12, color: TEXT_DIM, marginBottom: 12 }}>
                {apolloResults.total.toLocaleString()} result{apolloResults.total !== 1 ? 's' : ''} found
                {apolloResults.total_pages > 1 && ` (page ${apolloResults.page} of ${apolloResults.total_pages})`}
              </div>

              {(apolloResults.people || []).length === 0 && (
                <div style={{ textAlign: 'center', padding: 24, color: TEXT_DIM, fontSize: 13 }}>
                  No matches found. Try broadening your search.
                </div>
              )}

              <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                {(apolloResults.people || []).map(person => (
                  <div key={person.apollo_id} style={{
                    padding: '10px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 0,
                    marginBottom: 6, border: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'space-between',
                    alignItems: 'flex-start', gap: 10
                  }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: TEXT }}>{person.name}</div>
                      <div style={{ fontSize: 11, color: TEXT_MID, marginTop: 2 }}>{person.title}</div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                        {person.seniority && (
                          <span style={{ fontSize: 9, color: '#a78bfa', background: 'rgba(167,139,250,0.1)', padding: '1px 6px', borderRadius: 0 }}>
                            {person.seniority}
                          </span>
                        )}
                        {person.org_name && (
                          <span style={{ fontSize: 9, color: TEXT_DIM, background: 'rgba(255,255,255,0.05)', padding: '1px 6px', borderRadius: 0 }}>
                            {person.org_name}
                          </span>
                        )}
                        {person.city && (
                          <span style={{ fontSize: 9, color: TEXT_DIM, background: 'rgba(255,255,255,0.05)', padding: '1px 6px', borderRadius: 0 }}>
                            {[person.city, person.country].filter(Boolean).join(', ')}
                          </span>
                        )}
                        {person.linkedin_url && (
                          <a href={person.linkedin_url} target="_blank" rel="noreferrer"
                            style={{ fontSize: 9, color: '#60a5fa', textDecoration: 'none', background: 'rgba(96,165,250,0.1)', padding: '1px 6px', borderRadius: 0 }}>
                            LinkedIn
                          </a>
                        )}
                        {person.email_status && (
                          <span style={{ fontSize: 9, color: person.email_status === 'verified' ? '#4ade80' : '#fbbf24', background: person.email_status === 'verified' ? 'rgba(74,222,128,0.1)' : 'rgba(251,191,36,0.1)', padding: '1px 6px', borderRadius: 0 }}>
                            {person.email_status}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => enrichAndAddContact(person, company.id)}
                      disabled={apolloEnriching[person.apollo_id]}
                      style={{
                        fontSize: 11, color: '#fff', background: apolloEnriching[person.apollo_id] ? '#525252' : ACCENT,
                        border: 'none', borderRadius: 0, padding: '6px 12px', cursor: apolloEnriching[person.apollo_id] ? 'default' : 'pointer',
                        fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0
                      }}>
                      {apolloEnriching[person.apollo_id] ? 'Adding...' : '+ Add'}
                    </button>
                  </div>
                ))}
              </div>

              {apolloResults.total_pages > 1 && (
                <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'center' }}>
                  {apolloResults.page > 1 && (
                    <button onClick={() => searchApollo(company.name, null, apolloResults.page - 1)}
                      style={{ ...btnSecondaryStyle, padding: '6px 14px', fontSize: 12 }}>Previous</button>
                  )}
                  {apolloResults.page < apolloResults.total_pages && (
                    <button onClick={() => searchApollo(company.name, null, apolloResults.page + 1)}
                      style={{ ...btnSecondaryStyle, padding: '6px 14px', fontSize: 12 }}>Next</button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // ─── SHARED STYLES ────────────────────────────────────────────
  const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16, backdropFilter: 'blur(4px)' };
  const modalBox = { background: SURFACE, borderRadius: 0, padding: 24, maxWidth: 480, width: '100%', maxHeight: '85vh', overflow: 'auto', border: `1px solid ${BORDER}` };
  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 0, border: `1px solid ${BORDER}`, fontSize: 13, marginBottom: 10, fontFamily: 'inherit', fontWeight: 300, outline: 'none', boxSizing: 'border-box', background: BG, color: TEXT };
  const selectStyle = { width: '100%', padding: '10px 12px', borderRadius: 0, border: `1px solid ${BORDER}`, fontSize: 13, marginBottom: 10, background: BG, fontFamily: 'inherit', fontWeight: 300, color: TEXT_MID };
  const btnPrimary = { background: ACCENT, color: '#fff', border: 'none', borderRadius: 0, padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', letterSpacing: 0.3 };
  const btnSecondary = { background: 'transparent', color: TEXT_MID, border: `1px solid ${BORDER}`, borderRadius: 0, padding: '10px 20px', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' };
  const btnPrimaryStyle = btnPrimary;
  const btnSecondaryStyle = btnSecondary;

  return (
    <div style={{ background: BG, minHeight: '100vh', display: 'flex', flexDirection: 'column', zoom: 1.15 }}>
      <nav style={{ background: SURFACE, padding: '0 16px', height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${BORDER}`, position: 'sticky', top: 0, zIndex: 100, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/bejo-logo-light.png" alt="Bejo" style={{ height: 16, opacity: 0.8 }} />
          <div style={{ width: 1, height: 20, background: BORDER, margin: '0 4px' }} />
          <div>
            <div style={{ color: TEXT, fontSize: 13, fontWeight: 600, letterSpacing: 0.3 }}>Work Experience & Internship Outreach Tracker</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
          {['kanban', 'companies', 'settings'].map(v => (
            <button key={v} onClick={() => setView(v)} style={{
              background: view === v ? ACCENT : 'transparent',
              color: '#fff',
              border: view === v ? `1px solid ${ACCENT}` : `1px solid ${BORDER}`,
              borderRadius: 0, padding: '5px 12px', fontSize: 11, fontWeight: 500, cursor: 'pointer',
              fontFamily: 'inherit', letterSpacing: 0.3
            }}>
              {v === 'kanban' ? 'Board' : v === 'companies' ? 'Companies' : 'Settings'}
            </button>
          ))}
          <a href="/logout" style={{
            color: '#fff', border: `1px solid ${BORDER}`,
            borderRadius: 0, padding: '5px 12px', fontSize: 11, fontWeight: 500, textDecoration: 'none',
            marginLeft: 4
          }}>Logout</a>
        </div>
      </nav>

      <div style={{ padding: '0 16px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {view === 'kanban' && <KanbanBoard />}
        {view === 'companies' && <div style={{ maxWidth: 1200, margin: '0 auto', width: '100%', paddingTop: 16 }}><CompanyList /></div>}
        {view === 'settings' && <SettingsView />}
      </div>

      <AddContactModal />
      <ActivityModal />
      <HistoryModal />
      <CompanyDetailModal />
      <ApolloSearchModal />
    </div>
  );
}

export default App;
